import React, { useEffect } from "react";
import { ContentURL, getContentUrl } from "../../Utils/ContentURL";
import { BookingStatus } from "../../../Services/BookingEntities";
import { useAppState } from "../../../Redux/ReduxHooks";
import moment from "moment";

/**
 * Displays the booking status. This is updated whenever a status change is detected.
 */
export const BookingStatusDisplay: React.FC = () => {

    // This component opens only if 'PhoneToCnp.Booking' is non-null.
    const bookingDetails = useAppState(state => state.PhoneToCnp.Booking)!;

    useEffect(() => {
        if (!bookingDetails) return;
    }, [bookingDetails, bookingDetails?.TrackingInfo.BookingStatusID]);

    const statusId = bookingDetails.TrackingInfo.BookingStatusID;

    let statusText = "";
    let statusClass = "booking-status";

    // Status display text and colour varies based on the dispatch status and time to pickup.
    if (statusId == BookingStatus.NotDispatched) {
        // For ASAP bookings (IsImmediate), the system is actively searching for a driver even while
        // the status is still NotDispatched (e.g. after job is converted to fixed price).
        // Display "Searching for driver" instead of computing a countdown from the pickup time,
        // which would produce a negative value (e.g. "In -3 min") since the ASAP pickup time
        // is already in the past by the time the user views this page.
        if (bookingDetails.Timing.IsImmediate) {
            statusText = "Searching for driver";
        } else {
            const pickupTime = moment(bookingDetails.Timing.Time);
            const now = moment();
            const timeToPickupInMinutes = pickupTime.diff(now, "minutes");
            const timeToPickupInDays = pickupTime.diff(now, "days");

            // If the pickup is less than 1 hour away → show Relative time (e.g. in 45 min)
            if (timeToPickupInMinutes < 60) {
                statusText = `In ${timeToPickupInMinutes} min`;
            }
            // If the pickup is later the same day → show Today + time (e.g. Today, 3:43 pm)
            else if (pickupTime.isSame(now, 'day')) {
                statusText = `Today, ${pickupTime.format("h:mm A")}`;
            }
            // If the pickup is the next calendar day → show Tomorrow + time (e.g. Tomorrow, 9:30 am)
            else if (pickupTime.isSame(now.clone().add(1, 'day'), 'day')) {
                statusText = `Tomorrow, ${pickupTime.format("h:mm A")}`;
            }
            // If the pickup is the day after tomorrow and less than week → show Day + date + time (e.g. Thursday 15, 4:30 pm)
            else if (timeToPickupInDays >= 1 && timeToPickupInDays < 7) {
                statusText = `${pickupTime.format("dddd DD, h:mm A")}`;
            }
            // If the pickup is after a week from today → show Day + date + month + time (e.g. Thursday 22 May, 4:30 pm)
            else if (timeToPickupInDays >= 7) {
                statusText = `${pickupTime.format("dddd DD MMM, h:mm A")}`;
            }

            statusClass = "booking-status scheduled-or-completed";
        }
    }
    else if (statusId >= BookingStatus.Dispatching && statusId < BookingStatus.Accepted) {
        statusText = "Searching for driver";
    }
    else if (statusId == BookingStatus.Accepted) {
        const eta = bookingDetails.Eta;
        if (eta && eta.EtaDisplayText) {
            // Calculate pickup time by adding ETA seconds to current time
            const pickupTime = moment().add(eta.OptimisticEtaSeconds, 'seconds');
            statusText = `Pickup at ${pickupTime.format("h:mm A")} (in ${eta.EtaDisplayText})`;
        } else {
            statusText = "Driver on the way";
        }

        statusClass = "booking-status accepted";
    }
    else if (statusId == BookingStatus.PickedUp) {
        const eta = bookingDetails.Eta;
        if (eta && eta.EtaDisplayText) {
            // Calculate dropoff time by adding ETA seconds to current time
            const dropoffTime = moment().add(eta.OptimisticEtaSeconds, 'seconds');
            statusText = `Drop-off at ${dropoffTime.format("h:mm A")} (in ${eta.EtaDisplayText})`;
        } else {
            statusText = "On the way to destination";
        }

        statusClass = "booking-status picked-up";
    }
    else if (statusId == BookingStatus.Completed) {
        statusText = "Trip completed";
        statusClass = "booking-status scheduled-or-completed";
    }
    else if (statusId == BookingStatus.Cancelled) {
        statusText = "Trip cancelled";
        statusClass = "booking-status cancelled";
    }
    else if (statusId == BookingStatus.NoJob) {
        statusText = "Trip cancelled (no-show)";
        statusClass = "booking-status no-job";
    }

    return (
        <div className="booking-status-and-reference">
            <div className={statusClass}>{statusText}</div>
            {(statusId >= BookingStatus.Dispatching && statusId < BookingStatus.Accepted || (statusId === BookingStatus.NotDispatched && bookingDetails.Timing.IsImmediate)) && <div className="loader"><img src={getContentUrl(ContentURL.images.PhoneToCnp.Loader)} /></div>}
        </div>
    )
}